/**
 * The Nursys operations of the legacy bridge.
 *
 * Main's SPA reaches Nursys only through `src/api/nursysApi.js` and the roster
 * screens, which post keyon's `NursyInfo` / `ManageNurseList` envelopes and
 * read keyon's answers: a bare array of licence records, or an object whose
 * `status` is one of 'Result Not Found', 'No Server Response', 'Rate Limited',
 * 'Nurse Added', 'Nurse Not Added', or `{status:'Error', code, message}`.
 * `classifyLookup` / `classifyManage` sort those into FOUND / NOT_ENROLLED /
 * REJECTED / UNAVAILABLE, and only NOT_ENROLLED may lead to an enrolment. So
 * every answer the API gives is re-emitted here in exactly keyon's shape.
 *
 * WHICH FAILURES RESOLVE AND WHICH REJECT. keyon answers a Nursys outage, a
 * block and an unconfigured environment with HTTP 200 bodies, so those
 * resolve. It answers its gate's refusals (NursysAccess) with a real 401/403
 * and a sentence, and main's callers read those in their rejection arm
 * (`refusedRequestMessage`), so those reject, axios-shaped, with keyon's
 * sentence — only after the API client's own token refresh has failed. Any
 * other 5xx, or no response, rejects with no body: "could not reach".
 *
 * Kept out of `bridgeMap.js` so the Nursys rules are read in one place and
 * tested on their own (`__tests__/nursysBridge.spec.js`).
 */

import api, { ApiError } from './client';
import { bool, id, routeId } from './bridgeShapes';

/**
 * Longer than the SPA's default 30 s for the calls that wait on NCSBN.
 *
 * The API bounds a whole eNotify transaction — submit and poll — at 25 s so it
 * always answers first (NursysClient::TRANSACTION_DEADLINE_SECONDS); this is
 * the margin for the request around it, so a slow-but-valid answer is never
 * turned into a browser-side "could not reach" while the API goes on to write
 * the result.
 */
export const NURSYS_TIMEOUT_MS = 45_000;

/** keyon's NursysAccess::MESSAGES, which main's callers show as-is. */
export const REFUSAL_SENTENCES = {
  unauthenticated: 'Your session has ended. Please sign in again, or open the link from your invitation email.',
  forbidden: 'You do not have permission to do this.',
};

/** keyon's answer when the transaction did not settle (processClass NursysGetNurseInfo). */
export const NO_SERVER_RESPONSE = { status: 'No Server Response', message: 'Try Again in 2 minutes' };

/** keyon's SendNurseSettings refusal, word for word (decision H6). */
export const SETTINGS_RETIRED = {
  status: 'Error',
  code: 'retired',
  message: 'Scheduled licence re-verification is not available. Verify nurses from the nurse list.',
};

/** An axios-shaped rejection, which is what main's rejection arms read. */
export function axiosRejection(status, data) {
  const error = new Error(`Request failed with status code ${status}`);

  error.isAxiosError = true;
  error.response = { status, data, statusText: '', headers: {} };

  return error;
}

/**
 * keyon's answer for a Nursys call the API refused or could not complete —
 * resolved for the three keyon answers with 200, thrown for the rest.
 */
export function nursysFailure(error) {
  if (!(error instanceof ApiError)) throw error;

  if (error.code === 'registry_unavailable') return { ...NO_SERVER_RESPONSE };
  if (error.code === 'rate_limited' || error.status === 429) return { status: 'Rate Limited', message: error.message };
  if (error.code === 'not_configured') return { status: 'Error', code: 'not_configured', message: error.message };

  if (error.status === 401) {
    throw axiosRejection(401, { status: 'Error', code: 'unauthenticated', message: REFUSAL_SENTENCES.unauthenticated });
  }

  if (error.status === 403) {
    // insufficient_permission, insufficient_scope and forbidden are one thing
    // to main's refusals.js, which knows only `forbidden`.
    throw axiosRejection(403, { status: 'Error', code: 'forbidden', message: REFUSAL_SENTENCES.forbidden });
  }

  if (!error.status) {
    const network = new Error('Network Error');
    network.isAxiosError = true;
    throw network;
  }

  if (error.status >= 500) throw axiosRejection(error.status, undefined);

  return null;
}

/**
 * One licence record in keyon's field names (processClass GetNurseLookupData):
 * `Active` is Nursys's YES/NO, `LicenseOriginalIssueDate` is the service's
 * LicenseOriginalDate, and `NcsbnId` is the response-level id on every row.
 */
export function keyonLicence(licence, ncsbnId) {
  let active = '';
  if (licence.is_active === true) active = 'YES';
  if (licence.is_active === false) active = 'NO';

  return {
    FirstName: licence.first_name ?? null,
    LastName: licence.last_name ?? null,
    LicenseType: licence.license_type ?? null,
    JurisdictionAbbreviation: licence.jurisdiction ?? null,
    Jurisdiction: licence.jurisdiction_name ?? null,
    LicenseNumber: licence.license_number ?? null,
    Active: active,
    LicenseStatus: licence.license_status ?? null,
    LicenseExpirationDate: licence.expires_on ?? null,
    CompactStatus: licence.compact_status ?? null,
    Messages: licence.messages || [],
    NurseLookupDisciplines: licence.disciplines || [],
    NurseLookupNotifications: licence.notifications || [],
    NurseLookupAdvancedPractices: licence.advanced_practices || [],
    LicenseOriginalIssueDate: licence.original_issue_date ?? null,
    NcsbnId: ncsbnId ?? null,
  };
}

/** A lookup answer in keyon's shape: the bare array, or 'Result Not Found' with NCSBN's errors. */
export function keyonLookupAnswer(lookup) {
  if (lookup && lookup.outcome === 'found') {
    return (lookup.licences || []).map((licence) => keyonLicence(licence, lookup.ncsbn_id));
  }

  return {
    status: 'Result Not Found',
    msg: ((lookup && lookup.errors) || []).map((e) => ({ ErrorId: e.id, ErrorMessage: e.message })),
  };
}

/** `NursyInfo.LookupNursesInfo` — POST /nursys-lookups. */
export async function lookupNursesInfo(p) {
  try {
    const lookup = await api.post('/nursys-lookups', {
      license_number: p.rnlicense || p.licensnum,
      license_type: p.ltype,
      jurisdiction: p.jurisdiction,
      ncsbn_id: p.ncsid || undefined,
    }, { timeout: NURSYS_TIMEOUT_MS });

    return keyonLookupAnswer(lookup);
  } catch (error) {
    const answer = nursysFailure(error);

    // A request the API could not accept (a missing licence type, say) is
    // not an answer from Nursys; main's classifier reads this as UNAVAILABLE.
    return answer || { status: 'Error', code: error.code, message: error.message };
  }
}

/**
 * `ManageNurseList.Add_Nurse` — POST /nursys-registrations.
 *
 * `user_id` only when the caller names a real account: main's roster screens
 * enrol before the account exists (Add_Nurse, then InternalAddNurse), and
 * `recid` is NCSBN's RecordId, never a user. The two eNotify mail flags are
 * always stated, absent meaning "N" (decision H4).
 */
export async function addNurse(p) {
  try {
    await api.post('/nursys-registrations', {
      user_id: routeId(p.unique_ID) ?? undefined,
      license_number: p.licensnum,
      license_type: p.ltype,
      jurisdiction: p.jurisdiction,
      ncsbn_id: p.ncsid || undefined,
      birth_year: p.bdayear ? String(p.bdayear).split('-')[0] : undefined,
      last_four_ssn: p.lastfourssn || undefined,
      notification_enabled: bool(p.notficationenabled),
      reminder_enabled: bool(p.reminderenabled),
      practice_setting: p.hospitalpracsetting || undefined,
      practice_setting_other: p.hostpracsettingother || undefined,
      address_line1: p.address1 || undefined,
      address_line2: p.address2 || undefined,
      city: p.city || undefined,
      state: p.state || undefined,
      postal_code: p.zip || undefined,
      email: p.email || undefined,
    }, { timeout: NURSYS_TIMEOUT_MS });

    return { status: 'Nurse Added', ManageNurseListRequstObj: null };
  } catch (error) {
    if (error instanceof ApiError && error.code === 'nursys_rejected') {
      const errors = ((error.details && error.details.errors) || []).map((e) => ({ ErrorId: e.id, ErrorMessage: e.message }));

      return { status: 'Nurse Not Added', errors, requestObject: null };
    }

    const answer = nursysFailure(error);

    if (answer) return answer;

    // Already on this organization's list (409), or a request the API could
    // not accept (422): NCSBN was not asked, and the enrolment is not made.
    return {
      status: 'Nurse Not Added',
      errors: [{ ErrorId: null, ErrorMessage: error.message }],
      requestObject: null,
    };
  }
}

/**
 * `ManageNurseList.DeleteNurse` — take the nurse off our eNotify list.
 *
 * The call sites send the licence, the jurisdiction and the type (never a
 * registration id), so the enrolment is found by those first. Main answers a
 * removal with keyon's shared "Nurse Added" string and the call site then
 * deletes the account; a licence this organization never enrolled has nothing
 * to withdraw, so the same answer lets that follow-up run.
 */
export async function deleteNurse(p) {
  try {
    const page = await api.list('/nursys-registrations', {
      'filter[license_number]': p.licensnum,
      'filter[jurisdiction]': p.jurisdiction ? String(p.jurisdiction).toUpperCase() : undefined,
      'filter[license_type]': p.ltype || undefined,
      per_page: 5,
    });

    const registration = (page.items || [])[0];

    if (registration) {
      await api.delete(`/nursys-registrations/${id(registration.id)}`, { timeout: NURSYS_TIMEOUT_MS });
    }

    return { status: 'Nurse Added', ManageNurseListRequstObj: null };
  } catch (error) {
    const answer = nursysFailure(error);

    return answer || { status: 'Nurse Not Added', errors: [{ ErrorId: null, ErrorMessage: error.message }] };
  }
}

/**
 * `ManageNurseList.SendNurseSettings` — retired (decision H6). keyon answers
 * HTTP 410 with this body rather than saving a preference nothing honours.
 */
export async function sendNurseSettings() {
  throw axiosRejection(410, { ...SETTINGS_RETIRED });
}

/**
 * The roster rows main's LookupBatchNursesInfo answers with, from the
 * enrolments the run answered about. `activeStatus` is Nursys's own Active
 * flag — never derived from the status string (which read every UNENCUMBERED
 * nurse as "NO") and never the account status.
 */
export function nursysRosterRows(rows) {
  return (rows || []).map((row) => {
    let activeStatus = '';
    if (row.is_license_active === true) activeStatus = 'YES';
    if (row.is_license_active === false) activeStatus = 'NO';

    return {
      firstname: (row.user && row.user.first_name) || '',
      lastname: (row.user && row.user.last_name) || '',
      licensetype: row.license_type,
      licensenum: row.license_number,
      activeStatus,
      licensestatus: row.last_license_status || '',
      licensexpirationDt: row.license_expires_on || '',
    };
  });
}

/**
 * Wait for a roster run, within a bound. Main's call is synchronous and its
 * four handlers treat the answer as final; the port's run is a queue batch of
 * at most ten, so the wait is here. A run still going when the bound expires
 * is reported as not finished rather than as complete.
 */
export async function awaitNursysRun(run, { timeoutMs = 60_000, intervalMs = 1_500 } = {}) {
  const jobId = run && run.job_id;

  if (!jobId) return run;

  const deadline = Date.now() + timeoutMs;
  let latest = run;

  while (latest && latest.status === 'running' && Date.now() < deadline) {
    await new Promise((resolve) => { setTimeout(resolve, intervalMs); });

    latest = await api.get(`/nursys-lookup-batches/${encodeURIComponent(jobId)}`);
  }

  return latest;
}

/**
 * `NursyInfo.LookupBatchNursesInfo` — main's object, never "Completed!" for a
 * run that was blocked, capped or still going:
 * `{status, checked, written, truncated, rateLimited, message, results}`.
 */
export async function lookupBatchNursesInfo(p, { wait = awaitNursysRun } = {}) {
  const requested = (p.registration_ids || []).map(routeId).filter((n) => n !== null);

  try {
    const started = await api.post('/nursys-lookup-batches', requested.length > 0 ? { registration_ids: requested } : {});
    const run = (await wait(started)) || started;
    const stillRunning = run.status === 'running';

    return {
      status: run.rate_limited ? 'Rate Limited' : 'OK',
      checked: run.checked || 0,
      written: run.written || 0,
      // Main's handlers say "verify the rest in a few minutes" for a
      // truncated run, which is also the truth about one still going.
      truncated: Boolean(run.truncated) || stillRunning,
      rateLimited: Boolean(run.rate_limited),
      message: run.message || (stillRunning ? 'Verification is still running; the nurses checked so far have been saved.' : ''),
      results: nursysRosterRows(run.results),
    };
  } catch (error) {
    const answer = nursysFailure(error);

    if (answer && answer.status === 'Rate Limited') {
      // `run_in_progress` is the API declining to start a second run for the
      // organization, not NCSBN blocking us: main's handlers show the
      // sentence either way, but `rateLimited` stays keyon's meaning.
      return {
        status: 'Rate Limited',
        checked: 0,
        written: 0,
        truncated: false,
        rateLimited: error.code !== 'run_in_progress',
        message: answer.message,
        results: [],
      };
    }

    throw error;
  }
}

/**
 * The audit page's Nursys method names, as the filters the port's audit
 * trail can answer. Every one of them now records a row (the API writes the
 * Nursys disclosures itself, whatever came back), so a filter offered is a
 * filter that matches.
 *
 * `endpoint` matches the request path; `action` separates an enrolment
 * (`disclose`) from a removal (`delete`) on the same path.
 */
export const NURSYS_AUDIT_FILTERS = {
  LookupNursesInfo: { endpoint: 'nursys-lookups' },
  LookupBatchNursesInfo: { endpoint: 'nursys-lookup-batches' },
  Add_Nurse: { endpoint: 'nursys-registrations', action: 'disclose' },
  DeleteNurse: { endpoint: 'nursys-registrations', action: 'delete' },
  DeleteNurseInternal: { endpoint: 'api/v1/users/', action: 'delete' },
};
